"use client";

import { useState } from "react";
import Link from "next/link";
import ViewContentTracker from "@/components/shared/ViewContentTracker";
import { categories, services } from "./data";

export default function ServicesPage() {
  const [activeTab, setActiveTab] = useState("print");

  const filteredServices = services.filter((s) => s.category === activeTab);

  return (
    <>
      <ViewContentTracker contentName="Services" contentCategory="Services" />
      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <p className="text-brand-red text-[11px] uppercase tracking-[0.45em] font-semibold mb-6">
            Our Services
          </p>
          <h1 className="font-headline text-[clamp(64px,12vw,140px)] uppercase leading-none mb-8">
            Tailored Solutions
          </h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto mb-16 leading-relaxed">
            From custom apparel and premium signage to digital, explore our
            full suite of professional services.
          </p>

          {/* Tab Switcher — rectangular, no rounding */}
          <div className="flex flex-wrap justify-center max-w-2xl mx-auto border border-white/20">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                className={`flex-1 min-w-[120px] px-6 py-4 text-[11px] uppercase tracking-[0.2em] font-semibold transition-all duration-200 border-r border-white/20 last:border-r-0 ${
                  activeTab === cat.id
                    ? "bg-brand-orange text-white"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SERVICE CARDS ────────────────────────────────────── */}
      <section className="bg-white min-h-[600px] py-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
            {filteredServices.map((service) => (
              <div
                key={service.title}
                className="group flex flex-col bg-white p-10 hover:bg-black transition-all duration-300"
              >
                {/* Red dot accent */}
                <div className="w-2 h-2 bg-brand-orange group-hover:bg-brand-orange-light mb-6 transition-colors shrink-0" />

                <h2 className="text-2xl font-black text-gray-900 mb-4 group-hover:text-white transition-colors uppercase tracking-tight">
                  {service.title}
                </h2>
                <p className="text-gray-500 leading-relaxed mb-8 flex-grow text-sm group-hover:text-gray-400 transition-colors">
                  {service.desc}
                </p>
                <ul className="space-y-2 mb-10">
                  {service.features.map((feat) => (
                    <li
                      key={feat}
                      className="flex items-center gap-3 text-gray-500 text-xs group-hover:text-gray-500 transition-colors"
                    >
                      <div className="w-1 h-1 bg-brand-orange shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>
                {service.externalUrl ? (
                  <a
                    href={service.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] uppercase tracking-[0.25em] font-semibold text-brand-orange group-hover:text-brand-orange-light transition-colors flex items-center gap-2 mt-auto"
                  >
                    Visit Digital Site
                    <span className="group-hover:translate-x-1 transition-transform">
                      ↗
                    </span>
                  </a>
                ) : (
                  <Link
                    href={`/services/${service.slug}`}
                    className="text-[10px] uppercase tracking-[0.25em] font-semibold text-brand-orange group-hover:text-brand-orange-light transition-colors flex items-center gap-2 mt-auto"
                  >
                    Learn More
                    <span className="group-hover:translate-x-1 transition-transform">
                      →
                    </span>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BOTTOM CTA ───────────────────────────────────────── */}
      <section className="py-28 bg-black text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-brand-red text-[11px] uppercase tracking-[0.45em] font-semibold mb-6">
            Get Started
          </p>
          <h2 className="font-headline text-[clamp(56px,10vw,120px)] text-white uppercase leading-none mb-8">
            Ready to Scale?
          </h2>
          <p className="text-gray-500 text-lg mb-14 max-w-xl mx-auto leading-relaxed">
            Whether you need custom apparel or premium signage,
            our team is ready to deliver.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/order" className="btn-outline-white">
              Start Your Order
            </Link>
            <Link
              href="/contact-us"
              className="inline-flex items-center justify-center border border-white/20 text-white/50 text-[11px] uppercase tracking-[0.25em] font-semibold px-12 py-4 hover:border-white hover:text-white transition-all duration-200"
            >
              Talk to an Expert
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
